import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import {
  catchError,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  race,
  take,
  tap,
  timer,
} from 'rxjs';
import { BaseService } from 'src/app/core/services/base.httpService';
import { isRolesPermissionsEnforcementEnabled } from 'src/app/core/config/roles-permissions-enforcement.config';
import { SvgIconProp } from 'src/app/shared/models/dynamic.model';
import { applyActionPermissionsToIcons, ApplyRpeActionPermissionsOptions } from 'src/app/shared/rpe/rpe-action.util';
import { isActionInModuleRpeCatalog } from 'src/app/shared/rpe/rpe-module-action-catalog';
import {
  applyFieldPermissionsToForm,
  isFieldDisabledByPermissions,
  isFieldEnforcementActive,
  matchesFieldPermission,
  getRegistryBinding,
} from 'src/app/shared/rpe/rpe-field.util';
import {
  RpeFieldBinding,
  RpeFieldRegistry,
  RpeFieldRegistryEntry,
  RpeFormScope,
} from 'src/app/shared/rpe/rpe.model';
import { handleErrorMessage } from 'src/app/shared/operators/error.operator';
import { pageType, StatusCodes, STORAGE_KEYS, USER_PROFILE_ALL_FACILITIES_ID } from 'src/app/shared/utility/constants';
import { LocalStorageService } from 'src/app/shared/services/local-storage.service';
import { URLConstants } from 'src/app/shared/utility/url-constants';
import {
  getAllFacilitiesMergeLocationIds,
  getAssignedFacilityIds,
  isMultiWarehouseAllFacilitiesMode,
  mergePermissionContexts,
  resolveEffectivePermissionLocationId,
  resolveProfileActiveLocationId,
} from 'src/app/shared/utility/user-profile-all-facilities.util';
import {
  getAllFacilitiesBlockedActionMessage,
  isFacilityScopedActionBlockedForAllFacilitiesDefault,
} from 'src/app/shared/utility/all-facilities-facility-scoped-actions.util';
import { BAG_RPE_ACTION_GATEWAY_BY_INCOMING_CONTAINER, BAG_RPE_MODULE } from 'src/app/features/components/bag/bag-rpe.constants';
import { HAWB_RPE_ACTION_GATEWAY, HAWB_RPE_MODULE } from 'src/app/features/components/hawb/hawb-rpe.constants';
import { UserProfileInfo } from '../components/user-profile/user-profile.model';
import {
  EffectivePermissionsData,
  ModulePermission,
  PermissionContext,
} from 'src/app/shared/rpe/rpe-permissions.model';
import { UserEffectivePermissionsStateModel } from '../store/user-effective-permissions/user-effective-permissions.model';
import {
  ClearEffectivePermissions,
  SetActivePermissionLocation,
  SetEffectivePermissionsFailure,
  SetEffectivePermissionsLoadStarted,
  SetEffectivePermissionsSuccess,
} from '../store/user-effective-permissions/user-effective-permissions.actions';
import {
  ENFORCED_PERMISSION_ROUTES,
  HOME_ROUTE,
  PERMISSIONS_BOOTSTRAP_TIMEOUT_MS,
  PERMISSIONS_FALLBACK_ROUTE,
  PERMITTED_ROUTE_PRIORITY,
  resolveDashboardCodeFromActionName,
  resolveModuleRoute,
  DASHBOARD_ACTION_NAME_TO_CODE,
  isReportTypePermittedByActionNames,
} from '../store/user-effective-permissions/user-effective-permissions.constants';
import { UserEffectivePermissionsSelectors } from '../store/user-effective-permissions/user-effective-permissions.selectors';
import {
  MODULE_TO_OLDER_SPECIAL_PERMISSION,
  OTHER_PERMISSIONS_MODULE_NAME,
  SPECIAL_PERMISSION_ACTION_NAMES,
} from 'src/app/shared/special-permissions/special-permissions.constants';
import {
  getSmPageNameForRoute,
  isSetupMaintenanceParentModuleName,
  matchesSetupMaintenanceRoutePageName,
  normalizeSetupMaintenancePageName,
  SM_ALL_ROUTE_KEYS,
  SM_SETUP_AND_MAINTENANCE_PARENT_MODULE_NAMES,
} from 'src/app/shared/rpe/sm-rpe.constants';
import {
  AP_RPE_ACTION_OVERRIDE_INVOICE_DATE_LIMITS,
  AP_RPE_MODULE_NAMES,
} from 'src/app/shared/rpe/ap-rpe.constants';

/** Normalized empty response so errorHandler sees Success and FR-012 stays silent (no toast). */
const SILENT_PERMISSIONS_LOAD_FAILURE = {
  statusCode: StatusCodes.Success,
  data: { permissionContexts: [] as PermissionContext[] },
  __rpeLoadFailed: true as const,
};

@Injectable({
  providedIn: 'root',
})
export class UserEffectivePermissionsService {
  private readonly baseService = inject(BaseService);
  private readonly store = inject(Store);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly errorHandler = handleErrorMessage();

  public readonly permissionContexts$ = this.store.select(
    UserEffectivePermissionsSelectors.permissionContexts
  );

  public readonly permissionsLoaded$ = this.store.select(
    UserEffectivePermissionsSelectors.permissionsResolved
  );

  public isEnforcementEnabled(): boolean {
    return isRolesPermissionsEnforcementEnabled();
  }

  public isLoggedInSuperAdmin(): boolean {
    const profile = this.localStorageService.getItem(
      STORAGE_KEYS.USER_PROFILE_CACHE
    ) as { isSuperAdmin?: unknown } | null;
    return profile?.isSuperAdmin === true;
  }

  /**
   * FR-041 — View-mode mutate for info tabs (roles/notes/attachments/status codes).
   * Enforcement off → legacy allow. Super admin → allow. Otherwise deny.
   */
  public canMutateInformationTabsInViewMode(): boolean {
    if (!this.isEnforcementEnabled()) {
      return true;
    }
    return this.isLoggedInSuperAdmin();
  }

  /** Default Facility = All with 2+ assigned warehouses (not super admin). */
  public isMultiWarehouseAllFacilitiesDefault(): boolean {
    const state = this.store.selectSnapshot(
      UserEffectivePermissionsSelectors.state
    );
    return isMultiWarehouseAllFacilitiesMode(
      this.readCachedUserProfile(),
      state.activeLocationId,
      state.permissionContexts ?? []
    );
  }

  public isFacilityScopedActionBlocked(
    moduleName: string,
    actionName: string
  ): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return false;
    }
    if (!this.isMultiWarehouseAllFacilitiesDefault()) {
      return false;
    }
    return isFacilityScopedActionBlockedForAllFacilitiesDefault(
      moduleName,
      actionName
    );
  }

  public getFacilityScopedActionBlockMessage(
    moduleName: string,
    actionName: string
  ): string | undefined {
    return getAllFacilitiesBlockedActionMessage(moduleName, actionName);
  }

  public ensureLoaded(userId?: number): void {
    if (!this.isEnforcementEnabled()) {
      return;
    }
    const snapshot = this.store.selectSnapshot(
      UserEffectivePermissionsSelectors.state
    );
    if (snapshot.permissionsResolved) {
      return;
    }
    if (snapshot.loadStarted) {
      return;
    }
    if (userId == null) {
      return;
    }
    this.store.dispatch(new SetEffectivePermissionsLoadStarted());
    this.loadEffectivePermissions(userId).subscribe();
  }

  public loadEffectivePermissions(userId: number): Observable<void> {
    return this.baseService
      .initiateApiRequest(
        'GET',
        URLConstants.loadEffectivePermissions,
        '',
        'json'
      )
      .pipe(
        catchError(() => of(SILENT_PERMISSIONS_LOAD_FAILURE)),
        map((response: any) => {
          if (response?.__rpeLoadFailed) {
            return response;
          }
          if (response?.statusCode === StatusCodes.Success) {
            return response;
          }
          return SILENT_PERMISSIONS_LOAD_FAILURE;
        }),
        this.errorHandler(),
        tap((response: any) => {
          if (response?.__rpeLoadFailed) {
            this.store.dispatch(new SetEffectivePermissionsFailure(userId));
            return;
          }
          const data = response.data as EffectivePermissionsData;
          this.store.dispatch(
            new SetEffectivePermissionsSuccess(
              userId,
              data?.permissionContexts ?? []
            )
          );
        }),
        map(() => undefined)
      );
  }

  public setActiveLocationId(locationId: number | null | undefined): void {
    const normalized =
      locationId != null && !Number.isNaN(Number(locationId))
        ? Number(locationId)
        : null;
    this.store.dispatch(new SetActivePermissionLocation(normalized));
  }

  public getActiveContext(): PermissionContext | undefined {
    return this.resolveActiveContext();
  }

  /** @deprecated View-only-all mode removed — use union permissions + facility-scoped blocks. */
  public isAllFacilitiesViewOnlyMode(): boolean {
    return false;
  }

  public isGatewayBlockedForAllFacilitiesDefault(): boolean {
    return this.isFacilityScopedActionBlocked(
      HAWB_RPE_MODULE,
      HAWB_RPE_ACTION_GATEWAY
    );
  }

  public isBulkGatewayBlockedForAllFacilitiesDefault(): boolean {
    return this.isFacilityScopedActionBlocked(
      HAWB_RPE_MODULE,
      'bulk gateway'
    );
  }

  public isPublishBlockedForAllFacilitiesDefault(
    moduleName: 'Manifest' | 'MAWB'
  ): boolean {
    return this.isFacilityScopedActionBlocked(moduleName, 'publish');
  }

  public isIncomingContainerGatewayBlockedForAllFacilitiesDefault(): boolean {
    return this.isFacilityScopedActionBlocked(
      BAG_RPE_MODULE,
      BAG_RPE_ACTION_GATEWAY_BY_INCOMING_CONTAINER
    );
  }

  public getAssignedFacilityIds(): number[] {
    return getAssignedFacilityIds(this.readCachedUserProfile());
  }

  private readCachedUserProfile(): UserProfileInfo | null {
    const profile = this.localStorageService.getItem(
      STORAGE_KEYS.USER_PROFILE_CACHE
    ) as UserProfileInfo | null;
    return profile ?? null;
  }

  /**
   * Store active location, falling back to cached profile default until NGXS catches up.
   * Prevents enforced routes from denying access during profile/permissions bootstrap (FR-012).
   */
  private resolveActiveLocationIdForContext(
    state: UserEffectivePermissionsStateModel
  ): number | null {
    const profile = this.readCachedUserProfile();
    const contexts = state.permissionContexts ?? [];
    const storeLocationId = Number(state.activeLocationId);

    if (Number.isFinite(storeLocationId)) {
      const resolved = resolveEffectivePermissionLocationId(
        profile,
        storeLocationId,
        contexts
      );
      if (resolved != null) {
        return resolved;
      }
    }

    return resolveProfileActiveLocationId(profile, contexts);
  }

  private findPermissionContextByLocationId(
    contexts: PermissionContext[],
    locationId: number
  ): PermissionContext | undefined {
    const normalizedLocationId = Number(locationId);
    return contexts.find(
      (ctx) => Number(ctx.locationId) === normalizedLocationId
    );
  }

  private isRuntimePermissionEvaluationReady(): boolean {
    const state = this.store.selectSnapshot(
      UserEffectivePermissionsSelectors.state
    );
    if (!state.permissionsResolved) {
      return false;
    }
    return this.resolveActiveLocationIdForContext(state) != null;
  }

  private resolveActiveContext(): PermissionContext | undefined {
    const state = this.store.selectSnapshot(
      UserEffectivePermissionsSelectors.state
    );
    const activeLocationId = this.resolveActiveLocationIdForContext(state);
    const contexts = state.permissionContexts ?? [];

    if (activeLocationId == null) {
      return undefined;
    }

    const profile = this.readCachedUserProfile();
    const mergeIds = getAllFacilitiesMergeLocationIds(profile, contexts);
    const effectiveLocationId = resolveEffectivePermissionLocationId(
      profile,
      activeLocationId,
      contexts
    );

    if (
      effectiveLocationId === USER_PROFILE_ALL_FACILITIES_ID &&
      mergeIds.length >= 2
    ) {
      return mergePermissionContexts(contexts, mergeIds);
    }

    if (effectiveLocationId == null) {
      return undefined;
    }

    return this.findPermissionContextByLocationId(
      contexts,
      effectiveLocationId
    );
  }

  public getPermissionContexts(): PermissionContext[] {
    return (
      this.store.selectSnapshot(
        UserEffectivePermissionsSelectors.permissionContexts
      ) ?? []
    );
  }

  /** Dashboard types permitted at the active location context. */
  public getPermittedDashboardTypeCodes(): Set<string> {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return new Set(Object.values(DASHBOARD_ACTION_NAME_TO_CODE));
    }
    const dashboard = this.getModuleByNameFromActiveContext('Dashboard');
    const codes = new Set<string>();
    for (const action of dashboard?.actionPermissions ?? []) {
      const code = resolveDashboardCodeFromActionName(action.actionName);
      if (code) {
        codes.add(code);
      }
    }
    return codes;
  }

  public getPermittedReportActionNames(): string[] {
    if (!this.isEnforcementEnabled()) {
      return [];
    }
    const reports = this.getModuleByNameFromActiveContext('Reports');
    return (reports?.actionPermissions ?? []).map((action) => action.actionName);
  }

  public isReportTypePermitted(reportName: string): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    return isReportTypePermittedByActionNames(
      this.getPermittedReportActionNames(),
      reportName
    );
  }

  private getModuleByNameFromActiveContext(
    moduleName: string
  ): ModulePermission | undefined {
    return this.getActiveContext()?.modules.find(
      (module) =>
        module.moduleName === moduleName &&
        !this.isSetupMaintenancePageScopedEntry(module)
    );
  }

  /** S&M row with pageId > 0 — permissions are per page, not shared moduleId. */
  private isSetupMaintenancePageScopedEntry(
    module: ModulePermission
  ): boolean {
    return (
      isSetupMaintenanceParentModuleName(module.moduleName) &&
      module.pageId != null &&
      module.pageId > 0
    );
  }

  private hasContextSetupMaintenancePageRows(): boolean {
    const active = this.getActiveContext();
    if (!active) {
      return false;
    }
    return active.modules.some(
      (module) =>
        this.isSetupMaintenancePageScopedEntry(module)
    );
  }

  public getSetupMaintenancePagePermission(
    routeSegment: string
  ): ModulePermission | undefined {
    const pageName = getSmPageNameForRoute(routeSegment);
    if (!pageName) {
      return undefined;
    }
    const normalizedPageName = normalizeSetupMaintenancePageName(pageName);
    return this.getActiveContext()?.modules.find(
      (module) =>
        this.isSetupMaintenancePageScopedEntry(module) &&
        !!module.pageName &&
        normalizeSetupMaintenancePageName(module.pageName) === normalizedPageName
    );
  }

  private hasActionOnModule(
    module: ModulePermission | undefined,
    actionName: string
  ): boolean {
    if (!module) {
      return false;
    }
    const normalized = actionName.trim().toLowerCase();
    // D-RPE-UI-030: action not in module R&P catalog → accessible (do not gate).
    if (isActionInModuleRpeCatalog(module.moduleName, normalized) === false) {
      return true;
    }
    if (!module.actionPermissions?.length) {
      return false;
    }
    return module.actionPermissions.some(
      (action) => action.actionName?.toLowerCase() === normalized
    );
  }

  private modulePermitsRoute(
    module: ModulePermission | undefined,
    routerLink: string
  ): boolean {
    if (!module?.actionPermissions?.length) {
      return false;
    }
    return resolveModuleRoute(module.moduleName) === routerLink;
  }

  public isEnforcedRoute(routerLink: string): boolean {
    if (!this.isEnforcementEnabled()) {
      return false;
    }
    return ENFORCED_PERMISSION_ROUTES.has(routerLink);
  }

  public isMenuItemVisible(routerLink: string): boolean {
    if (routerLink === HOME_ROUTE) {
      return true;
    }
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    if (!this.isRuntimePermissionEvaluationReady()) {
      return true;
    }
    if (!this.isEnforcedRoute(routerLink)) {
      return true;
    }
    return this.isRoutePermitted(routerLink);
  }

  /** Path segments from router URL (no query/hash). */
  public parseUrlPathSegments(url: string): string[] {
    const path = url.split('?')[0].split('#')[0];
    return path.split('/').filter(Boolean);
  }

  /** Whether the current URL is allowed at the active permission context (FR-011, FR-010). */
  public isUrlPermitted(url: string): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    const segments = this.parseUrlPathSegments(url);
    const routeSegment = segments[0] ?? '';
    if (!this.isEnforcedRoute(routeSegment)) {
      return true;
    }
    if (!this.isRuntimePermissionEvaluationReady()) {
      return true;
    }
    if (routeSegment === pageType.setupAndManitenance && segments.length > 1) {
      return this.isSetupMaintenanceSubmoduleRoutePermitted(segments[1]);
    }
    return this.isRoutePermitted(routeSegment);
  }

  /**
   * Re-evaluate current route when permissions resolve or active location changes (FR-010).
   * Wired from `AppComponent` bootstrap.
   */
  public startEnforcedRouteAccessMonitor(
    router: Router,
    destroyRef: DestroyRef
  ): void {
    if (!this.isEnforcementEnabled()) {
      return;
    }

    const reevaluate = (): void => {
      this.redirectIfCurrentRouteDenied(router);
    };

    combineLatest([
      this.store.select(UserEffectivePermissionsSelectors.permissionsResolved),
      this.store.select(UserEffectivePermissionsSelectors.activeLocationId),
    ])
      .pipe(
        filter(([resolved]) => resolved),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(() => reevaluate());

    router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(() => reevaluate());
  }

  private redirectIfCurrentRouteDenied(router: Router): void {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return;
    }
    if (this.isUrlPermitted(router.url)) {
      return;
    }
    const routeSegment = this.parseUrlPathSegments(router.url)[0] ?? '';
    void router.navigate(this.getAccessDeniedRedirect(routeSegment));
  }

  public isRoutePermitted(routerLink: string): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    const permissionsResolved = this.store.selectSnapshot(
      UserEffectivePermissionsSelectors.permissionsResolved
    );
    if (!permissionsResolved) {
      return true;
    }

    if (routerLink === pageType.setupAndManitenance) {
      return this.hasSetupMaintenanceAccess();
    }

    const active = this.getActiveContext();
    if (!active) {
      return false;
    }
    return active.modules.some((module) => this.modulePermitsRoute(module, routerLink));
  }

  public hasSetupMaintenanceAccess(): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    if (this.hasContextSetupMaintenancePageRows()) {
      return SM_ALL_ROUTE_KEYS.some((route) =>
        this.isSetupMaintenanceSubmoduleRoutePermitted(route)
      );
    }
    return SM_SETUP_AND_MAINTENANCE_PARENT_MODULE_NAMES.some((name) =>
      this.hasModuleWithActions(name)
    );
  }

  private hasModuleWithActions(moduleName: string): boolean {
    return !!this.getModuleByName(moduleName)?.actionPermissions?.length;
  }

  public isSetupMaintenanceSubmoduleRoutePermitted(
    routeSegment: string
  ): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    const page = this.getSetupMaintenancePagePermission(routeSegment);
    if (page) {
      return !!page.actionPermissions?.length;
    }
    if (this.hasContextSetupMaintenancePageRows()) {
      return false;
    }
    return SM_SETUP_AND_MAINTENANCE_PARENT_MODULE_NAMES.some((name) =>
      this.hasModuleWithActions(name)
    );
  }

  public hasSetupMaintenancePageAction(
    routeSegment: string,
    actionName: string
  ): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    const page = this.getSetupMaintenancePagePermission(routeSegment);
    if (page) {
      return this.hasActionOnModule(page, actionName);
    }
    if (this.hasContextSetupMaintenancePageRows()) {
      return false;
    }
    return this.hasSetupMaintenanceParentModuleAction(actionName);
  }

  private hasSetupMaintenanceParentModuleAction(
    actionName: string
  ): boolean {
    for (const name of SM_SETUP_AND_MAINTENANCE_PARENT_MODULE_NAMES) {
      if (this.hasActionOnModule(this.getModuleByName(name), actionName)) {
        return true;
      }
    }
    return false;
  }

  public applySetupMaintenancePageActionPermissionsToIcons(
    routeSegment: string,
    icons: SvgIconProp[],
    options?: ApplyRpeActionPermissionsOptions
  ): SvgIconProp[] {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return icons;
    }
    const page = this.getSetupMaintenancePagePermission(routeSegment);
    if (page) {
      return applyActionPermissionsToIcons(
        icons,
        (actionName) => this.hasActionOnModule(page, actionName),
        options
      );
    }
    if (this.hasContextSetupMaintenancePageRows()) {
      return applyActionPermissionsToIcons(
        icons,
        () => false,
        { ...options, hideUnmapped: false }
      );
    }
    for (const name of SM_SETUP_AND_MAINTENANCE_PARENT_MODULE_NAMES) {
      const parent = this.getModuleByName(name);
      if (parent?.actionPermissions?.length) {
        return applyActionPermissionsToIcons(
          icons,
          (actionName) => this.hasActionOnModule(parent, actionName),
          options
        );
      }
    }
    return applyActionPermissionsToIcons(icons, () => false, {
      ...options,
      hideUnmapped: false,
    });
  }

  public getPermittedRoutes(): string[] {
    const routes = new Set<string>();

    const active = this.getActiveContext();
    if (active) {
      for (const module of active.modules) {
        if (!module.actionPermissions?.length) {
          continue;
        }
        const route = resolveModuleRoute(module.moduleName);
        if (route) {
          routes.add(route);
        }
      }
    }

    if (this.hasSetupMaintenanceAccess()) {
      routes.add(pageType.setupAndManitenance);
    }

    return PERMITTED_ROUTE_PRIORITY.filter((route) => routes.has(route));
  }

  /** First allowed module route, or home when none (FR-011). */
  public getAccessDeniedRedirect(excludedRoute?: string): string[] {
    for (const route of PERMITTED_ROUTE_PRIORITY) {
      if (route === excludedRoute) {
        continue;
      }
      if (this.isRoutePermitted(route)) {
        return [route];
      }
    }
    return [PERMISSIONS_FALLBACK_ROUTE];
  }

  public getDefaultLandingRoute(): string[] {
    if (
      !this.isEnforcementEnabled() ||
      this.isLoggedInSuperAdmin()
    ) {
      return ['dashboard'];
    }
    return this.getAccessDeniedRedirect();
  }

  public waitUntilResolved(): Observable<boolean> {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return of(true);
    }
    const resolved = this.store.selectSnapshot(
      UserEffectivePermissionsSelectors.permissionsResolved
    );
    if (resolved) {
      return of(true);
    }
    return race([
      this.permissionsLoaded$.pipe(
        filter((loaded) => loaded),
        take(1),
        map(() => true)
      ),
      timer(PERMISSIONS_BOOTSTRAP_TIMEOUT_MS).pipe(
        tap(() => this.abandonPermissionsBootstrap()),
        map(() => true)
      ),
    ]);
  }

  /**
   * Unblock guards when profile or permissions API never completes (FR-012, F-006).
   * Sets empty contexts with permissionsResolved true.
   */
  public abandonPermissionsBootstrap(userId?: number): void {
    if (!this.isEnforcementEnabled()) {
      return;
    }
    const snapshot = this.store.selectSnapshot(
      UserEffectivePermissionsSelectors.state
    );
    if (snapshot.permissionsResolved) {
      return;
    }
    const resolvedUserId =
      userId ?? snapshot.userId ?? this.readCachedProfileUserId() ?? 0;
    this.store.dispatch(new SetEffectivePermissionsFailure(resolvedUserId));
  }

  private readCachedProfileUserId(): number | undefined {
    const profile = this.localStorageService.getItem(
      STORAGE_KEYS.USER_PROFILE_CACHE
    ) as { userId?: unknown } | null;
    const id = Number(profile?.userId);
    return Number.isFinite(id) && id > 0 ? id : undefined;
  }

  public getModuleByName(moduleName: string): ModulePermission | undefined {
    return this.getModuleByNameFromActiveContext(moduleName);
  }

  public getOtherPermissionsModule(): ModulePermission | undefined {
    return this.getModuleByName(OTHER_PERMISSIONS_MODULE_NAME);
  }

  public hasOtherPermissionAction(actionName: string): boolean {
    if (!this.isEnforcementEnabled()) {
      return false;
    }
    return this.hasAction(OTHER_PERMISSIONS_MODULE_NAME, actionName);
  }

  public hasOtherPermissionById(actionId: number): boolean {
    if (!this.isEnforcementEnabled()) {
      return false;
    }
    const otherModule = this.getOtherPermissionsModule();
    return (
      otherModule?.actionPermissions?.some(
        action => Number(action.actionId) === Number(actionId)
      ) ?? false
    );
  }

  public hasOlderRecordSpecialPermission(moduleName: string): boolean {
    const actionName = MODULE_TO_OLDER_SPECIAL_PERMISSION[moduleName];
    return actionName ? this.hasOtherPermissionAction(actionName) : false;
  }

  /** Super admin, RPE off, or older-record special — skip SPE edit icon / age gating. */
  public shouldBypassSpecialPermissionsEditBlocking(
    moduleName?: string
  ): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    return moduleName
      ? this.hasOlderRecordSpecialPermission(moduleName)
      : false;
  }

  public hasExtendedMawbDatePermission(): boolean {
    return this.hasOtherPermissionAction(
      SPECIAL_PERMISSION_ACTION_NAMES.EDIT_MAWB_DATE_EXTENDED
    );
  }

  public hasExtendedManifestDatePermission(): boolean {
    return this.hasOtherPermissionAction(
      SPECIAL_PERMISSION_ACTION_NAMES.EDIT_MANIFEST_DATE_EXTENDED
    );
  }

  /**
   * Elevated AP invoice date override. When RPE is off, default limits stay on
   * (same fail-closed pattern as Other Permissions specials).
   */
  public canOverrideApInvoiceDateLimits(): boolean {
    if (!this.isEnforcementEnabled()) {
      return false;
    }
    return this.hasModuleAction(
      AP_RPE_MODULE_NAMES,
      AP_RPE_ACTION_OVERRIDE_INVOICE_DATE_LIMITS
    );
  }

  public hasSpainShipmentPermission(): boolean {
    return this.hasOtherPermissionAction(
      SPECIAL_PERMISSION_ACTION_NAMES.HANDLE_SPAIN_SHIPMENTS
    );
  }

  public hasHighValueShipmentPermission(): boolean {
    return this.hasOtherPermissionAction(
      SPECIAL_PERMISSION_ACTION_NAMES.HANDLE_HIGH_VALUE_SHIPMENTS
    );
  }

  public hasGatewayUnclearedCbpPermission(): boolean {
    return (
      this.hasOtherPermissionAction(
        SPECIAL_PERMISSION_ACTION_NAMES.GATEWAY_UNCLEARED_CBP_SHIPMENTS
      ) ||
      this.hasOtherPermissionAction(
        SPECIAL_PERMISSION_ACTION_NAMES.GATEWAY_HELD_CBP_SHIPMENTS
      )
    );
  }

  public shouldBypassFieldLevelRestrictions(moduleName: string): boolean {
    return this.hasOlderRecordSpecialPermission(moduleName);
  }

  /** First matching module name from API context, else first candidate (D-RPE-UI-005 aliases). */
  public resolveModuleName(moduleNames: readonly string[]): string {
    for (const name of moduleNames) {
      if (this.getModuleByName(name)) {
        return name;
      }
    }
    return moduleNames[0];
  }

  public hasModuleAction(
    moduleNames: readonly string[],
    actionName: string
  ): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    if (
      moduleNames.some((name) =>
        this.isFacilityScopedActionBlocked(name, actionName)
      )
    ) {
      return false;
    }
    return moduleNames.some((name) => this.hasAction(name, actionName));
  }

  public getModuleByRoute(routerLink: string): ModulePermission | undefined {
    return this.getActiveContext()?.modules.find(
      (module) => resolveModuleRoute(module.moduleName) === routerLink
    );
  }

  public hasAction(moduleName: string, actionName: string): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    const normalized = actionName.trim().toLowerCase();
    if (this.isFacilityScopedActionBlocked(moduleName, actionName)) {
      return false;
    }
    // D-RPE-UI-030: action not in module R&P catalog → accessible (do not gate).
    if (isActionInModuleRpeCatalog(moduleName, normalized) === false) {
      return true;
    }
    const module = this.getModuleByName(moduleName);
    if (!module?.actionPermissions?.length) {
      return false;
    }
    return module.actionPermissions.some(
      (action) => action.actionName?.toLowerCase() === normalized
    );
  }

  public applyActionPermissionsToIcons(
    moduleName: string,
    icons: SvgIconProp[],
    options?: ApplyRpeActionPermissionsOptions
  ): SvgIconProp[] {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return icons;
    }
    return applyActionPermissionsToIcons(
      icons,
      (actionName) => this.hasAction(moduleName, actionName),
      options
    );
  }

  public hasFieldPermission(
    moduleName: string,
    binding: RpeFieldBinding
  ): boolean {
    if (!this.isEnforcementEnabled() || this.isLoggedInSuperAdmin()) {
      return true;
    }
    const module = this.getModuleByName(moduleName);
    if (!module?.fieldPermissions?.length) {
      return false;
    }
    return matchesFieldPermission(module.fieldPermissions, binding);
  }

  public isFieldEnforcementActive(
    moduleName: string,
    pageMode: string
  ): boolean {
    if (this.shouldBypassFieldLevelRestrictions(moduleName) ||this.isLoggedInSuperAdmin()) {
      return false;
    }
    const module = this.getModuleByName(moduleName);
    return isFieldEnforcementActive(
      this.isEnforcementEnabled(),
      pageMode,
      module?.fieldPermissions,
      (actionName) => this.hasAction(moduleName, actionName)
    );
  }

  public isFieldDisabled(
    moduleName: string,
    entry: RpeFieldRegistryEntry,
    pageMode: string
  ): boolean {
    const module = this.getModuleByName(moduleName);
    const binding = getRegistryBinding(entry, pageMode);
    return isFieldDisabledByPermissions(
      this.isFieldEnforcementActive(moduleName, pageMode),
      module?.fieldPermissions ?? [],
      binding,
      !!entry.matchByIdentityNameOnly
    );
  }

  public applyFieldPermissionsToForm(
    moduleName: string,
    form: FormGroup,
    registry: RpeFieldRegistry,
    pageMode: string,
    formScope?: RpeFormScope
  ): void {
    if (!this.isFieldEnforcementActive(moduleName, pageMode)) {
      return;
    }
    applyFieldPermissionsToForm(
      form,
      registry,
      pageMode,
      (entry) => this.isFieldDisabled(moduleName, entry, pageMode),
      formScope
    );
  }

  public clearPermissions(): void {
    this.store.dispatch(new ClearEffectivePermissions());
  }

  public subscribeLocationRefresh(
    destroyRef: DestroyRef,
    callback: () => void
  ): void {
    combineLatest([
      this.store.select(UserEffectivePermissionsSelectors.permissionsResolved),
      this.store.select(UserEffectivePermissionsSelectors.activeLocationId),
    ])
      .pipe(
        filter(([resolved]) => resolved),
        takeUntilDestroyed(destroyRef)
      ).subscribe(() => callback());
  }
}
